import * as React from 'react'
import { useParams, Link } from 'react-router-dom'
import ProtectedRoute from '../helpers/ProtectedRoute'
import Page from '../components/layout/Page'
import Content from '../components/layout/Content'
import Arrow from '../icons/Arrow'
import Plus from '../icons/Plus'
import Add from '../icons/Add'
import { getChannelName } from '../helpers/channelName'
import { fetchData } from '../utils/fetchData'
import { Image } from '../types/Image'
import { Media } from '../types/User'
import { Tag } from '../types/Tag'
import styles from '../styles/Library.module.css'
import ImageCard from '../components/ImageCard'
import FilterTags from '../components/FilterTags'

function Library() {
  const STORAGE_KEY = 'media'
  const localStorageMedia = localStorage.getItem(STORAGE_KEY)
  let media: Media | null = null
  if (localStorageMedia) {
    try {
      media = JSON.parse(localStorageMedia)
    } catch (error) {
      console.log(error)
    }
  }

  const showMore = React.useRef<HTMLDivElement>(null)
  const token = media?.token ?? null
  const { storeId, channelType } = useParams()
  const [images, setImages] = React.useState<Image[]>([])
  const [filteredImages, setFilteredImages] = React.useState<Image[]>([])
  const [showfiltered, setShowFiltered] = React.useState<boolean>(false)
  const [tags, setTags] = React.useState<Tag[]>([])
  const [selectedTag, setSelectedTag] = React.useState<number | null>(null)
  const [allTagsButton, setAllTagsButton] = React.useState<boolean>(true)
  const [untaggedButton, setUntaggedButton] = React.useState<boolean>(false)
  const [untaggedImages, setUntaggedImages] = React.useState<Image[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [noImages, setNoImages] = React.useState<boolean>(false)
  const [nextCursor, setNextCursor] = React.useState<string | null>(null)

  const getImages = React.useCallback(async () => {
    setLoading(true)
    try {
      if (nextCursor?.length === 0) {
        return
      }
      const res = await fetchData(`/api/cloudinary/store/${storeId}/channel/${channelType}/images${nextCursor ? `/${nextCursor}` : ''}`, {
        method: 'GET',
      })

      if(res) {
        const { data, status } = res

        if (status === 200 && data && data?.resources && data?.resources.length > 0) {
          setImages(prev => [...prev, ...data.resources.map(resource => {
            return { ...resource, tags: resource.tags.map(tag => tag.replace(/[[\]"]/g, ''))}
          })])
          setNextCursor(data.next_cursor ? data.next_cursor : '')
        }
        else if (data?.resources?.length === 0) {
          setNoImages(true)
        }
      }

    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [channelType, nextCursor, storeId])

  React.useEffect(() => {
    if (token && storeId && !nextCursor) {
      getImages()
    }
  }, [token, storeId, getImages, nextCursor])


  React.useEffect(() => {
    if (images) { 
      setUntaggedImages(images.filter((image) => image.tags.length === 1))
      let allTags = images.flatMap(({tags}) => tags.map((tag) => tag))
      const excludedTags = ['icacatering', 'icatogo', 'icapronto', 'icakiosk', 'other']
      allTags = allTags.filter(item => !excludedTags.includes(item))
      createTagList(allTags)
    }
  }, [images])

  const createTagList = (tags: string[]) => {
    const tagList: Tag[] = tags.map(item => {
      return {
        "tag": item,
        "count": getTagAmount(tags, item)
      }
    })
    getUniqueTags(tagList)
  }

  const getUniqueTags = (list: Tag[]) => {
    const uniqueTags = [...new Map(list.map(item => [item.tag, item])).values()]
    setTags(uniqueTags)
  }

  const getTagAmount = (allTags: string[], value: string) => {
    let count = 0
    allTags.forEach((tag: string) => (tag === value && count++))
    return count
  }

  const showAllImages = () => {
    setShowFiltered(false)
    setAllTagsButton(true)
    setSelectedTag(null)
    setUntaggedButton(false)
  }

  const showFilteredImages = (tag?: string, tagId?: number, untagged = false) => {
    setFilteredImages(images.filter((image) => {
      return image.tags.some((imageTag) => imageTag === tag)
    }))
    setShowFiltered(true)
    setAllTagsButton(false)
    setUntaggedButton(false)
    setSelectedTag(tagId as number)

    if (untagged) {
      setFilteredImages(untaggedImages)
      setUntaggedButton(true)
      setSelectedTag(null)
    }
  }

  return (
    <ProtectedRoute user={token}>
      <Page>
        <div className={styles.library}>
          <Content>
            <Link className={styles.backButton} to={`/${storeId}`}>
              <Arrow className={styles.arrowIcon} />
              <span>Till startsidan</span>
            </Link>
            <div className={styles.header}>
              <h1 className={styles.heading}>Mediabibliotek</h1>
              {channelType && <p className={styles.preamble}>Här hittar du alla bilder som butiken laddat upp under {getChannelName(channelType)}.</p>}
            </div>
            {(tags.length > 0 || untaggedImages.length > 0) && (
              <FilterTags 
                tags={tags}
                images={images}
                allTagsButton={allTagsButton}
                untaggedImages={untaggedImages}
                untaggedButton={untaggedButton}
                selectedTag={selectedTag}
                showAllImages={showAllImages}
                showFilteredImages={showFilteredImages}
              />
            )}
            {loading ? (
              <div>Hämtar bilder…</div>
            ) : (
              <> 
                {images.length > 0 && !showfiltered && (
                  <>
                    <div className={styles.subHeadingGroup}>
                      <h2 className={styles.subHeading}>Bilder ({images.length})</h2>
                      <Link className={styles.uploadButton} to={`/${storeId}/${channelType}/ladda-upp`}>
                        <Plus />
                        Ladda upp bild
                      </Link>
                    </div>
                    <ul className={styles.list}>
                      {images.map(
                        (image) => (
                          <ImageCard 
                            key={image.asset_id}
                            assetId={image.asset_id}
                            secureUrl={image.secure_url}
                            aspectRatio={image.aspect_ratio}
                            context={image.context}/>
                        )
                      )}
                    </ul>
                  </>
                )}
                {filteredImages.length > 0 && showfiltered && (
                  <>
                    <div className={styles.subHeadingGroup}>
                      <h2 className={styles.subHeading}>Bilder ({filteredImages.length})</h2>
                      <Link className={styles.uploadButton} to={`/${storeId}/${channelType}/ladda-upp`}>
                        <Plus />
                        Ladda upp bild
                      </Link>
                    </div><ul className={styles.list}>
                      {filteredImages.map(
                        (image) => (
                          <ImageCard 
                            key={image.asset_id}
                            assetId={image.asset_id}
                            secureUrl={image.secure_url}
                            aspectRatio={image.aspect_ratio}
                            context={image.context}/>
                        )
                      )}
                    </ul>
                  </>
                )}
                {noImages && (
                  <div className={styles.noImages}>
                    <h3 className={styles.noImagesText}>Det finns inga uppladdade bilder än.</h3>
                    <Link className={styles.uploadButton} to={`/${storeId}/${channelType}/ladda-upp`}>
                      <Plus />
                      Ladda upp bild
                    </Link>
                  </div>
                )}
              </>
            )}

            <div className={styles.pagination} ref={showMore}>
              {nextCursor && nextCursor.length > 0 &&
                <button disabled={loading} className={styles.frontButton} onClick={() => getImages()}>
                  {loading && images.length > 0 ?
                    <span>Laddar…</span> :
                    <>
                      <span>Visa fler</span>
                      <Add className={styles.addIcon} />
                    </>
                  }
                </button>
              }
            </div>
          </Content>
        </div>
      </Page>
    </ProtectedRoute>
  )
}

export default Library